<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import AddFunnel from './AddFunnel.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const funnels = useMapGetter('funnels/getFunnels');
const uiFlags = useMapGetter('funnels/getUIFlags');

const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const selectedFunnel = ref({});

const tableHeaders = computed(() => [
  t('FUNNELS.SETTINGS.TABLE.NAME'),
  t('FUNNELS.SETTINGS.TABLE.DESCRIPTION'),
  t('FUNNELS.SETTINGS.TABLE.STAGES'),
  t('FUNNELS.SETTINGS.TABLE.VISIBILITY'),
  t('FUNNELS.SETTINGS.TABLE.ACTIONS'),
]);

const visibilityLabel = funnel =>
  funnel.team_ids?.length
    ? t('FUNNELS.SETTINGS.VISIBILITY.TEAMS', { count: funnel.team_ids.length })
    : t('FUNNELS.SETTINGS.VISIBILITY.ALL');

const openManage = funnel => {
  router.push({
    name: 'settings_funnels_show',
    params: { funnelId: funnel.id },
  });
};

const openDeletePopup = funnel => {
  selectedFunnel.value = funnel;
  showDeletePopup.value = true;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const confirmDeletion = async () => {
  closeDeletePopup();
  try {
    await store.dispatch('funnels/deleteFunnel', selectedFunnel.value.id);
    useAlert(t('FUNNELS.SETTINGS.DELETE_CONFIRM.SUCCESS'));
  } catch (error) {
    useAlert(t('FUNNELS.SETTINGS.DELETE_CONFIRM.ERROR'));
  }
};

onBeforeMount(() => {
  store.dispatch('funnels/fetchFunnels');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetchingFunnels"
    :loading-message="t('FUNNELS.SETTINGS.LOADING')"
    :no-records-found="!funnels.length"
    :no-records-message="t('FUNNELS.SETTINGS.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('FUNNELS.SETTINGS.HEADER')"
        :description="t('FUNNELS.SETTINGS.DESCRIPTION')"
      >
        <template #actions>
          <Button
            :label="t('FUNNELS.SETTINGS.NEW')"
            size="sm"
            @click="showAddPopup = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="funnels"
        :no-data-message="t('FUNNELS.SETTINGS.EMPTY')"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="funnel in items" :key="funnel.id" :item="funnel">
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12">
                  {{ funnel.name }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ funnel.description }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ (funnel.stages || []).length }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ visibilityLabel(funnel) }}
                </span>
              </BaseTableCell>
              <BaseTableCell align="end">
                <div class="flex justify-end flex-shrink-0 gap-3">
                  <Button
                    :label="t('FUNNELS.SETTINGS.MANAGE')"
                    slate
                    faded
                    sm
                    @click="openManage(funnel)"
                  />
                  <Button
                    v-tooltip.top="t('FUNNELS.SETTINGS.DELETE')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="openDeletePopup(funnel)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal
      v-model:show="showAddPopup"
      :on-close="() => (showAddPopup = false)"
    >
      <AddFunnel @close="showAddPopup = false" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="t('FUNNELS.SETTINGS.DELETE_CONFIRM.TITLE')"
      :message="t('FUNNELS.SETTINGS.DELETE_CONFIRM.MESSAGE')"
      :message-value="` ${selectedFunnel.name}?`"
      :confirm-text="t('FUNNELS.SETTINGS.DELETE_CONFIRM.YES')"
      :reject-text="t('FUNNELS.SETTINGS.DELETE_CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
